import cloudflareAssets from "@/assets/cloudflareAssets";
import type { TransactionTypes } from "@/modules/transaction/common/enum";
import type {
  IOpenTransactionDetailsProps,
  ITransaction,
} from "@/modules/transaction/common/types";
import { TransactionActionModals } from "@/modules/transaction/components";
import { useTransactionColumns } from "@/modules/transaction/hooks";
import { useGetTransactionsQuery, useSignTransferMutation, } from "@/modules/transaction/services";
import { setRowId, setTransaction } from "@/modules/transaction/state";
import {
  AccessManagementDialogContent,
  INITIAL_PAGE,
  LOGIN_OTP_COUNT,
  PageSizeOptions,
} from "@/shared/common/constants";
import {
  AccessManagementKeys,
  CloudflareAsset,
  ErrorKeys,
  ErrorMessages,
  OperationStatus,
  TableNames
} from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import {
  AccessManagementDialog,
  CloudFlareImage,
  FilterPanel,
  OTPPopup,
  Select
} from "@/shared/components";
import { daysApart, getTableName } from "@/shared/helpers";
import {
  useAppDispatch,
  useAppSelector,
  useFilters,
  useMediaQuery,
  useNotify,
  useOperationActions,
  useTableHooks,
} from "@/shared/hooks";
import {
  AdvancedTable as Table,
  ColumnSettings,
} from "@ab.uitools/ui-library/components/AdvancedTable";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  openDetails: (data: IOpenTransactionDetailsProps) => void;
  closeDetails?: () => void;
}

const MAX_DIFF_DAYS = 2;

export const TransactionHistory = ({ openDetails, closeDetails }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    showErrorAlertNotification,
    clearSnackbarNotification,
    showSuccessSnackbar,
    showSnackbar,
  } = useNotify();
  const { filters, setFilters, defaultFilters } = useFilters({ dayCount: 7 });
  const snackbarNotification = useAppSelector((state) => state.notification.snackbarNotification);
  const activeItem = useAppSelector((state) => state.common);
  const transaction = useAppSelector((state) => state.transactions.transaction);
  const rowId = useAppSelector((state) => state.transactions.rowId);
  const [transferId, setTransferId] = useState<string>("");
  const [isOtpModalOpen, setIsOtpModalOpen] = useState<boolean>(false);
  const [isCantSignModalOpen, setIsCantSignModalOpen] = useState<boolean>(false);
  const [transactionHistory, setTransactionHistory] = useState<ITransaction[]>([]);
  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false);
  const [isAccessManagementOpen, setIsAccessManagementOpen] = useState<boolean>(false);

  const {
    page,
    pageSize,
    tableRef,
    hasNextPage,
    setHasNextPage,
    onNextPage,
    onPrevPage,
    onPageSizeChange,
    onPageIndexReset,
    updateHasNextPage,
  } = useTableHooks<ITransaction>();
  const {
    actions,
    isOpenSendToEdit,
    setIsOpenSendToEdit,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
  } = useOperationActions();

  const {
    data: list,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetTransactionsQuery({
    pageIndex: page,
    pageSize,
    ...filters,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const [signTransfer, { isLoading }] = useSignTransferMutation();

  const onSetRowData = (data: ITransaction, index: string) => {
    dispatch(setTransaction({ transaction: { ...data, index } }));
    dispatch(setRowId({ rowId: data.id }));
  };

  const onSign = (transaction: ITransaction, index: string) => {
    const newDate = new Date();
    const creationDate = new Date(transaction.creationDate);
    const diffDays = daysApart(newDate, creationDate);
    onSetRowData(transaction, index);

    if (
      (transaction.state.transactionStatus === OperationStatus.Created ||
        transaction.state.transactionStatus === OperationStatus.PartiallySigned) &&
      diffDays < MAX_DIFF_DAYS
    ) {
      setTransferId(transaction.id);
      setIsOtpModalOpen(true);
    } else {
      setIsCantSignModalOpen(true);
    }
  };

  const handleChangeToken = (token: string) => {
    if (token.length === LOGIN_OTP_COUNT) {
      signTransfer({
        id: transferId,
        otp: token,
        transactionType: transaction?.transactionType as string,
      }).unwrap()
        .then(() => {
          setIsOtpModalOpen(false);
          refetch();
          showSuccessSnackbar(t("alerts.transferHasBeenSuccessfullySigned"));
        })
        .catch((error) => {
          const warning = error?.data?.warnings?.[0];
          if (warning === ErrorKeys.TransactionRestrictedByStopTool) {
            setIsOtpModalOpen(false);
            setIsAccessManagementOpen(true);
          } else {
            showErrorAlertNotification(error);
          }
        });
    }
  };

  const closeMenu = () => {
    dispatch(setRowId(null));
    setIsOpenMenu(false);
  };

  const openMenu = (data: ITransaction, index: string) => {
    onSetRowData(data, index);
    setIsOpenMenu(true);
  };

  const onRowClick = (data: ITransaction, index: string) => {
    onSetRowData(data, index);
    openDetails({
      id: data.id,
      transactionType: data.transactionType as TransactionTypes,
    });
  };

  const isDesktop = useMediaQuery();
  const tableName = getTableName(TableNames.TransactionHistory);

  const columns = useTransactionColumns({
    rowId,
    onSign,
    actions,
    closeMenu,
    openMenu,
    isOpenMenu,
  });

  useEffect(() => {
    if (!list) return;

    if (list?.length || (!list?.length && page === INITIAL_PAGE)) {
      setTransactionHistory(list);
      updateHasNextPage(list.length);
    } else if (!list?.length && page !== INITIAL_PAGE) {
      onPrevPage();
      setHasNextPage(false);
    }
  }, [list]);

  useEffect(() => {
    if (snackbarNotification?.message) {
      showSnackbar({
        message: snackbarNotification?.message || "",
        type: snackbarNotification?.type,
      });
    }
  }, [snackbarNotification]);

  useEffect(() => {
    if (
      activeItem.transactionId &&
      activeItem.activeTransactionType &&
      list?.length
    ) {
      const activeRowId = list?.findIndex(
        (t) => t.id === activeItem.transactionId
      );
      const activeRow = list?.find(
        (t) => t.id === activeItem.transactionId
      );
      dispatch(setRowId({ rowId: activeRow?.id }));
      dispatch(setTransaction({
        transaction: { ...activeRow as ITransaction, index: `${activeRowId}` }
      }));
      openDetails({
        id: `${activeItem.transactionId}`,
        transactionType: activeItem.activeTransactionType as TransactionTypes,
      });
    }
  }, [activeItem, list]);

  useEffect(() => {
    return () => {
      clearSnackbarNotification();
      dispatch(setTransaction(null));
      dispatch(setRowId(null));
    };
  }, []);

  return (
    <>
      <div className="page-body full-width flexbox justify-content--center">
        <div
          className={classNames("advanced_table-wrapper", {
            "table-full": transactionHistory.length,
            "table-empty": !transactionHistory.length,
          })}>
          <div className="full-width pb-24">
            <Heading className="content-title" weight="bolder" size="xsmall">
              {t("transactionHistoryTable.title")}
            </Heading>
          </div>
          <Table
            data={transactionHistory || []}
            withSelect
            isActionsVisible={!isDesktop}
            columns={columns}
            isLoading={isFetching || !tableName}
            onRowClick={(row) => onRowClick(row.original, row.id)}
            activeRowId={rowId ? `${transaction?.index}` : ""}
            tableSettings={{
              tableName: tableName || "",
              persistColumnSettings: "localStorage"
            }}
            hasError={isError}
            emptyTitle={
              isError ?
                (error as IError)?.data?.warnings?.[0] ||
                t(ErrorMessages.TryAgainShortly) :
                t("general.noData")
            }
            emptySubTitle={
              !isError ? t("general.noDataAvailable") : ""
            }
            emptyIllustration={cloudflareAssets[CloudflareAsset.EmptyContent]}
            renderHeader={(table) => (
              <div
                className="advanced-table__header flexbox justify-content--between align-items--center py-16 px-24">
                <FilterPanel
                  filters={filters}
                  setFilters={setFilters}
                  resetPagination={onPageIndexReset}
                  defaultFilters={defaultFilters}
                />
                <div className="flexbox">
                  <ColumnSettings
                    table={table}
                    allToggleText={t("general.all")}
                    menuIconTooltipText={t("general.settings")}
                    tooltipText={t("general.columnCantBeHidden")}
                  />
                </div>
              </div>
            )}
            renderFooter={(table) => {
              tableRef.current = table;
              return (
                <div className="advanced-table__footer">
                  <div className="advanced-table__pagination">
                    <Select
                      setSelectedValue={(value) => onPageSizeChange(`${value}`)}
                      selectedValue={`${pageSize}`}
                      options={PageSizeOptions}
                    />
                    <div className="flexbox align-items--center">
                      <Button
                        onClick={onPrevPage}
                        size="medium"
                        type="tertiary"
                        data-testid="transaction-history-prev-page"
                        data-ga-id="transaction-history-prev-page"
                        data-ga-action="Transfers history table previos page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortLeft}/>
                          ),
                        }}
                        disabled={page === INITIAL_PAGE}
                      />
                      <Button
                        onClick={onNextPage}
                        size="medium"
                        type="tertiary"
                        data-testid="transaction-history-next-page"
                        data-ga-id="transaction-history-next-page"
                        data-ga-action="Transfers history table next page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortRight}/>
                          ),
                        }}
                        disabled={!hasNextPage}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
      <OTPPopup
        togglePopup={setIsOtpModalOpen}
        isOpen={isOtpModalOpen}
        handleChangeToken={handleChangeToken}
        isLoading={isLoading}
      />
      <Modal
        isOpen={isCantSignModalOpen}
        onClose={() => setIsCantSignModalOpen(false)}
        onSubmit={() => setIsCantSignModalOpen(false)}
        className="cant-sign-modal"
        hasCancelBtn={false}
        buttonProps={{
          confirm: {
            buttonText: t("general.ok"),
          },
        }}
        titleProps={{
          title: t("general.isNotSubjectToSign"),
          size: "large"
        }}
      >
      </Modal>
      <TransactionActionModals
        onClose={closeDetails}
        isOpenSendToEdit={isOpenSendToEdit}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsOpenSendToEdit={setIsOpenSendToEdit}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
      />
      {transaction?.transactionType && (
        <AccessManagementDialog
          isOpen={isAccessManagementOpen}
          onClose={() => setIsAccessManagementOpen(false)}
          content={
            AccessManagementDialogContent[AccessManagementKeys[
              transaction.transactionType as keyof typeof AccessManagementKeys
              ]]
          }
        />
      )}
    </>
  );
};
